/**
 * Call ViewModel
 * Contains ALL business logic for the call feature
 */

import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSipContext } from "../../../services/sip";
import { Analytics } from "../../../services/analytics";
import { useAppSelector } from "../../../hooks";
import { selectSteContext } from "../../auth";
import { Routes } from "../../../routes";
import type { CallStatus, Contact } from "../model/CallModel";

// View State
export interface CallViewState {
  contact: Contact | null;
  status: CallStatus;
  duration: number;
  isMuted: boolean;
  isSpeakerOn: boolean;
  dtmfInput: string;
  showDtmfPad: boolean;
}

// View Actions
export interface CallViewActions {
  handleEndCall: () => void;
  handleToggleMute: () => void;
  handleToggleSpeaker: () => void;
  handleDtmfPress: (digit: string) => void;
  handleToggleDtmfPad: () => void;
  handleBackToContacts: () => void;
}

export interface CallViewModel extends CallViewState, CallViewActions {}

interface UseCallViewModelOptions {
  contact: Contact | null;
  onCallEnded?: () => void;
}

/**
 * Custom hook that provides the ViewModel for the Call screen
 */
export function useCallViewModel(
  options: UseCallViewModelOptions,
): CallViewModel {
  const { contact, onCallEnded } = options;
  const navigate = useNavigate();

  // Shared SIP context
  const sip = useSipContext();
  const steContext = useAppSelector(selectSteContext);

  // State
  // Derive status from SIP state instead of managing it separately
  const status = useMemo<CallStatus>(() => {
    if (sip.isCallInProgress) return "in-call";
    if (sip.isCallRinging) return "ringing";
    return "idle";
  }, [sip.isCallInProgress, sip.isCallRinging]);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [dtmfInput, setDtmfInput] = useState("");
  const [showDtmfPad, setShowDtmfPad] = useState(false);

  // Timer ref for call duration
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<Date | null>(null);
  const hasInitiatedCall = useRef(false);
  const callWasStarted = useRef(false); // Track if SIP ever confirmed the call started
  const callIdRef = useRef<string | null>(null); // Unique ID for this call session

  // Initiate the call
  const initiateCall = useCallback(() => {
    if (!contact || hasInitiatedCall.current) {
      console.log("[CallViewModel] initiateCall skipped:", {
        contact: !!contact,
        hasInitiated: hasInitiatedCall.current,
      });
      return;
    }
    console.log(
      "[CallViewModel] Initiating call for contact:",
      contact.id,
      contact.contactName,
    );
    hasInitiatedCall.current = true;
    callIdRef.current = crypto.randomUUID();
    Analytics.track("call_initiated", {
      call_id: callIdRef.current,
      contact_id: contact.id,
      contact_name:
        contact.contactName.en ||
        Object.values(contact.contactName)[0] ||
        "Unknown",
    });
    sip.makeCall(contact.id);
  }, [contact, sip]);

  // Handle side effects when call state changes (analytics, timer, navigation)
  useEffect(() => {
    console.log("[CallViewModel] SIP state:", {
      isCallRinging: sip.isCallRinging,
      isCallInProgress: sip.isCallInProgress,
      sipError: sip.error,
      status,
      callWasStarted: callWasStarted.current,
    });

    // Track when call starts ringing
    if (sip.isCallRinging && !callWasStarted.current) {
      callWasStarted.current = true;
    }

    // Handle call answered (transition to in-progress)
    if (sip.isCallInProgress && !startTimeRef.current) {
      callWasStarted.current = true;
      startTimeRef.current = new Date();
      Analytics.track("call_answered", {
        call_id: callIdRef.current,
        contact_id: contact?.id,
        contact_name:
          contact?.contactName?.en ||
          Object.values(contact?.contactName || {})[0] ||
          "Unknown",
      });
      // Start duration timer
      timerRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Math.floor(
            (Date.now() - startTimeRef.current.getTime()) / 1000,
          );
          setDuration(elapsed);
        }
      }, 1000);
    }

    // Handle call end or failure
    if (!sip.isCallInProgress && !sip.isCallRinging) {
      // Call ended or failed
      if (callWasStarted.current) {
        // Normal call end - call was connected at some point
        console.log("[CallViewModel] Call ended, navigating back");
        Analytics.track("call_ended", {
          call_id: callIdRef.current,
          contact_id: contact?.id,
          contact_name:
            contact?.contactName?.en ||
            Object.values(contact?.contactName || {})[0] ||
            "Unknown",
          duration: duration,
        });
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        navigate(Routes.HOME);
      } else if (sip.error && hasInitiatedCall.current) {
        // Call failed before connecting (e.g., 404 Not Found)
        console.log("[CallViewModel] Call failed, navigating back:", sip.error);
        Analytics.track("call_failed", {
          call_id: callIdRef.current,
          contact_id: contact?.id,
          contact_name:
            contact?.contactName?.en ||
            Object.values(contact?.contactName || {})[0] ||
            "Unknown",
          error: sip.error,
        });
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        navigate(Routes.HOME);
      }
    }
  }, [
    sip.isCallInProgress,
    sip.isCallRinging,
    sip.error,
    status,
    navigate,
    duration,
    contact,
  ]);

  // Start call on mount if connected
  useEffect(() => {
    console.log("[CallViewModel] Connection check:", {
      hasContact: !!contact,
      isConnected: sip.isConnected,
      hasInitiated: hasInitiatedCall.current,
    });
    // If not connected, connect first
    if (steContext && !sip.isConnected && !sip.isRegistered && !sip.error) {
      sip.connect();
    } else if (contact && sip.isConnected && !hasInitiatedCall.current) {
      initiateCall();
    }
  }, [
    contact,
    sip.isConnected,
    sip.isRegistered,
    sip.error,
    steContext,
    initiateCall,
    sip,
  ]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // End call - navigate back immediately
  // Guard against accidental clicks (Android phantom touch issue)
  const handleEndCall = useCallback(() => {
    console.log("[CallViewModel] handleEndCall invoked!", new Error().stack);
    try {
      sip.endCall();
    } catch (error) {
      console.warn("[CallViewModel] Error ending call:", error);
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    onCallEnded?.();
    navigate(Routes.HOME);
  }, [sip, onCallEnded, navigate]);

  // Toggle mute
  const handleToggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
    // TODO: Actually mute/unmute the audio track
  }, []);

  // Toggle speaker
  const handleToggleSpeaker = useCallback(() => {
    setIsSpeakerOn((prev) => !prev);
    // TODO: Actually toggle speaker
  }, []);

  // DTMF press
  const handleDtmfPress = useCallback(
    (digit: string) => {
      setDtmfInput((prev) => prev + digit);
      sip.sendDTMF(digit);
    },
    [sip],
  );

  // Toggle DTMF pad
  const handleToggleDtmfPad = useCallback(() => {
    setShowDtmfPad((prev) => !prev);
  }, []);

  // Back to contacts
  const handleBackToContacts = useCallback(() => {
    navigate(Routes.HOME);
  }, [navigate]);

  return {
    // State
    contact,
    status,
    duration,
    isMuted,
    isSpeakerOn,
    dtmfInput,
    showDtmfPad,

    // Actions
    handleEndCall,
    handleToggleMute,
    handleToggleSpeaker,
    handleDtmfPress,
    handleToggleDtmfPad,
    handleBackToContacts,
  };
}
